import { useEffect, useRef, useState } from 'react';
import type { AppVersionSummary, PublishRequest, PublishResult, UserAppDraft } from '@api/appBuilder';
import { isDraftConflict, manifestProblemsOf } from './appBuilderErrors';
import type { PublishReview } from './PublishReviewDialog';
import { useAppBuilderActions } from './useAppBuilder';

// An unchanged prompt publishes at once; only a judge call is slow enough to need a notice.
const REVIEW_NOTICE_DELAY_MS = 1000;

interface PublishFlowOptions {
  readonly draft: UserAppDraft;
  readonly onReload: () => void;
  /** The draft changed elsewhere and has just been reloaded. */
  readonly onConflict: () => void;
  readonly onProblems: (problems: string[]) => void;
  readonly onFailed: (err: unknown) => void;
  readonly onPublished: (version: number | null | undefined) => void;
  /** The draft is exactly what is live, so nothing was published. */
  readonly onUnchanged: (liveVersion: number | null | undefined) => void;
  readonly onRestored: (version: AppVersionSummary) => void;
}

/**
 * Publishing behind the judge: a block or a suggestion opens the review instead
 * of publishing. Applying a suggested prompt saves it (and remembers the old one
 * for Undo); "Publish anyway" overrides advice on the draft it was made on, so a
 * draft edited since is a 409 and a fresh publish reviews it again. A block has
 * no way past it but fixing the app.
 */
export const usePublishFlow = ({
  draft,
  onReload,
  onConflict,
  onProblems,
  onFailed,
  onPublished,
  onUnchanged,
  onRestored,
}: PublishFlowOptions) => {
  const actions = useAppBuilderActions();
  const [review, setReview] = useState<PublishReview | null>(null);
  const [undoPrompt, setUndoPrompt] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const reviewNotice = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { appKey } = draft;

  useEffect(() => () => clearTimeout(reviewNotice.current), []);

  const stopReviewNotice = () => {
    clearTimeout(reviewNotice.current);
    setReviewing(false);
  };

  const refused = (err: unknown) => {
    if (isDraftConflict(err)) {
      onReload();
      onConflict();
      return;
    }
    const listed = manifestProblemsOf(err);
    if (listed.length > 0) onProblems(listed);
    else onFailed(err);
  };

  const settle = (result: PublishResult) => {
    switch (result.outcome) {
      case 'PUBLISHED':
        setReview(null);
        setUndoPrompt(null);
        onPublished(result.version);
        return;
      case 'REVIEW_SUGGESTED':
        setReview({
          kind: 'advice',
          findings: result.findings ?? [],
          currentPrompt: draft.manifest.prompt ?? '',
          revisedPrompt: result.revisedPrompt ?? null,
          draftLockVersion: result.draftLockVersion ?? draft.lockVersion,
        });
        return;
      case 'BLOCKED':
        setReview({
          kind: 'blocked',
          findings: result.findings ?? [],
          currentPrompt: draft.manifest.prompt ?? '',
          revisedPrompt: null,
          draftLockVersion: draft.lockVersion,
        });
        return;
      case 'UNCHANGED':
        setReview(null);
        onUnchanged(result.version);
        return;
      default: {
        const unhandled: never = result.outcome;
        throw new Error(`Unhandled publish outcome: ${String(unhandled)}`);
      }
    }
  };

  const send = (request: PublishRequest) => {
    actions
      .publish({ appKey, request })
      .then((result) => {
        stopReviewNotice();
        settle(result);
      })
      .catch((err: unknown) => {
        stopReviewNotice();
        setReview(null);
        refused(err);
      });
  };

  const publish = () => {
    onProblems([]);
    clearTimeout(reviewNotice.current);
    reviewNotice.current = setTimeout(() => setReviewing(true), REVIEW_NOTICE_DELAY_MS);
    send({ expectedLockVersion: draft.lockVersion });
  };

  const publishAnyway = () => {
    if (review?.kind !== 'advice') return;
    send({ expectedLockVersion: review.draftLockVersion, skipReview: true });
  };

  const savePrompt = (prompt: string, expectedLockVersion: number) =>
    actions.updateDraft({ appKey, update: { expectedLockVersion, prompt } });

  const applySuggestion = () => {
    if (review?.revisedPrompt == null) return;
    const { currentPrompt } = review;
    savePrompt(review.revisedPrompt, review.draftLockVersion)
      .then(() => {
        setUndoPrompt(currentPrompt);
        setReview(null);
      })
      .catch((err: unknown) => {
        setReview(null);
        refused(err);
      });
  };

  const undoSuggestion = () => {
    if (undoPrompt === null) return;
    savePrompt(undoPrompt, draft.lockVersion)
      .then(() => setUndoPrompt(null))
      .catch(refused);
  };

  const restore = (version: AppVersionSummary) =>
    actions
      .restoreVersion({ appKey, version: version.version, expectedLockVersion: draft.lockVersion })
      .then(() => {
        onProblems([]);
        setUndoPrompt(null);
        onRestored(version);
      })
      .catch(refused);

  return {
    review,
    reviewing,
    suggestionApplied: undoPrompt !== null,
    publish,
    publishAnyway,
    applySuggestion,
    undoSuggestion,
    restore,
    closeReview: () => setReview(null),
    isPublishing: actions.isPublishing,
    isRestoring: actions.isRestoringVersion,
    isSavingPrompt: actions.isSavingDraft,
  };
};
